import type { MouseEventHandler, KeyboardEventHandler, ReactNode, Attributes } from "react";
import { cloneElement, isValidElement } from "react";
import type { CompositeStateReturn } from "reakit";

import { Box } from "../../Box/Box";

type ContentProps = {
  isVisible: boolean;
  mainContent: ReactNode[];
  supportContent: ReactNode[];
  selectedId: string | null;
  onOptionClick: MouseEventHandler<HTMLLIElement>;
  onOptionKeydown: KeyboardEventHandler<HTMLLIElement>;
} & CompositeStateReturn;

export function ListboxContent({
  isVisible,
  mainContent,
  supportContent,
  selectedId,
  onOptionClick,
  onOptionKeydown,
  ...composite
}: ContentProps) {
  const hasSupportContent = supportContent.some(isValidElement);

  if (hasSupportContent) return <Box aria-hidden>{supportContent}</Box>;

  const currentIndex = composite.currentId
    ? composite.items.findIndex(({ id }) => id === composite.currentId)
    : 0;
  const selectedIndex = selectedId ? composite.items.findIndex(({ id }) => id === selectedId) : -1;

  const handleOptionMouseEnter = (index: number) => {
    if (composite.currentId !== composite.items[index]?.id) {
      composite.setCurrentId(composite.items[index]?.id);
    }
  };

  return (
    <>
      {mainContent.map((child, index) => {
        if (!isValidElement(child)) {
          return child;
        }
        const isSelected = selectedIndex === index;
        return cloneElement(child, {
          ...composite,
          "aria-selected": (isSelected && isVisible) || undefined,
          "data-selected": isSelected,
          "data-current": currentIndex === index,
          onClick: onOptionClick,
          onKeyDown: onOptionKeydown,
          onMouseEnter: () => handleOptionMouseEnter(index),
        } as Partial<unknown> & Attributes);
      })}
    </>
  );
}
